'use client';

import { ArrowRight, Edit2, FileText, FolderOpen, Image, MapPin, MessageSquare, Triangle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams, useRouter } from 'next/navigation';
import React from 'react';

import { ResponsiveDialog } from '@ui/components/common/dialog/responsiveDialog';
import { cn } from '@ui/lib/utils';

interface EditActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activityId: string;
}

interface EditOption {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  action: 'navigate' | 'full-edit';
  path?: string;
}

export function EditActivityModal({ isOpen, onClose, activityId }: EditActivityModalProps) {
  const t = useTranslations('ActivityDetail');
  const router = useRouter();
  const params = useParams();
  const locale = params.locale as string;

  const editOptions: EditOption[] = [
    {
      id: 'title',
      title: t('editOptions.title'),
      icon: FileText,
      action: 'navigate',
      path: `/activity/${activityId}/edit?step=title`
    },
    {
      id: 'category',
      title: t('editOptions.category'),
      icon: FolderOpen,
      action: 'navigate',
      path: `/activity/${activityId}/edit?step=category`
    },
    {
      id: 'description',
      title: t('editOptions.description'),
      icon: MessageSquare,
      action: 'navigate',
      path: `/activity/${activityId}/edit?step=description`
    },
    {
      id: 'photos',
      title: t('editOptions.photos'),
      icon: Image,
      action: 'navigate',
      path: `/activity/${activityId}/edit?step=photos`
    },
    {
      id: 'disclaimer',
      title: t('editOptions.disclaimer'),
      icon: Triangle,
      action: 'navigate',
      path: `/activity/${activityId}/edit?step=disclaimer`
    },
    // Location is the LAST wizard step (it needs the saved activity id), so it sits
    // after Disclaimer here to mirror that order.
    {
      id: 'location',
      title: t('editOptions.location'),
      icon: MapPin,
      action: 'navigate',
      path: `/activity/${activityId}/edit?step=location`
    },
    {
      id: 'complete-edit',
      title: t('editOptions.editCompleteActivity'),
      icon: Edit2,
      action: 'full-edit',
      path: `/activity/${activityId}/edit`
    }
  ];

  const handleOptionClick = (option: EditOption) => {
    onClose();
    if (option.path) {
      router.push(`/${locale}${option.path}`);
    }
  };

  return (
    <ResponsiveDialog isOpen={isOpen} onClose={onClose} title={t('editActivity')} size="sm">
      <div className="space-y-0">
        {editOptions.map((option, index) => {
          const IconComponent = option.icon;
          const isLastOption = option.id === 'complete-edit';

          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleOptionClick(option)}
              className={cn(
                'flex items-center w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors',
                'border-0 bg-transparent',
                index === 0 && 'rounded-t-lg',
                index === editOptions.length - 1 && 'rounded-b-lg',
                !isLastOption && 'border-b border-gray-100'
              )}
            >
              <div className="flex items-center gap-4 w-full">
                {/* Icon - 20x20px as per Figma */}
                <div className="flex-shrink-0 w-5 h-5">
                  <IconComponent className="w-5 h-5 text-gray-700" />
                </div>

                {/* Title - Manrope 600, 14px, #333741 as per Figma */}
                <span
                  className="flex-1 font-semibold text-sm text-[#333741]"
                  style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '14px' }}
                >
                  {option.title}
                </span>

                {/* Arrow Right - positioned at the end */}
                <div className="flex-shrink-0 w-5 h-5">
                  <ArrowRight className="w-5 h-5 text-gray-700" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </ResponsiveDialog>
  );
}
